import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { IonItemSliding, IonItem, IonItemOptions, IonItemOption } from '@ionic/react';
import toast from 'react-hot-toast';
import { Icon } from '../../icons';
import { Badge, Btn, Card, MetricCard, SectionHeader, DataTable, FormRow, Field, Tabs, Modal, SearchBar, FormActions } from '../../components/ui';
import { ConfirmModal } from '../../components/shared';
import SalaryService from '../../api/salary';
import api from '../../api/axios';
import { formatDateByPreference } from '../../utils/formatters';
import { useSettings } from '../../context/SettingsContext';
import en from '../../locales/en.json';
import hi from '../../locales/hi.json';
import gu from '../../locales/gu.json';

const translations = { en, hi, gu };

export default function SalaryScreen() {
  const { language = 'en' } = useSettings();
  const t = (key, params) => {
    let str = translations[language]?.hr?.[key] || translations.en?.hr?.[key] || key;
    if (params) { Object.keys(params).forEach(p => { str = str.replace(`{${p}}`, params[p]); }); }
    return str;
  };
  const [tab, setTab] = useState(0);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [staffModal, setStaffModal] = useState(false);
  const [payslipModal, setPayslipModal] = useState(null);
  const [payrunModal, setPayrunModal] = useState(false);
  const [editStaff, setEditStaff] = useState(null);
  const [delStaff, setDelStaff] = useState(null);
  const [search, setSearch] = useState("");
  const [histStaff, setHistStaff] = useState(null);
  const [loading, setLoading] = useState(false);

  const defS = { name: "", email: "", role: "Security Guard", department: "Security", basic_salary: "", hra: "", travel_allowance: "", pf_deduction: "", esi_deduction: "", tds: "", bank_name: "", ifsc_code: "", account_number: "", joined_date: "", status: "Active" };
  const [sForm, setSForm] = useState(defS);
  const [sErrors, setSErrors] = useState({});

  const validateField = (field, value, formData = sForm) => {
    switch (field) {
      case 'name': {
        const val = (value ?? '').trim();
        if (!val) return t("val_name_required") || "Full Name is required";
        if (/\d/.test(val)) return t("val_name_invalid") || "Full Name cannot contain numbers";
        if (!/^[\p{L}\s.'-]+$/u.test(val)) return t("val_name_invalid") || "Full Name must be a valid name";
        if (val.length < 2) return t("val_name_min") || "Full Name must be at least 2 characters";
        return null;
      }
      case 'email': {
        const val = (value ?? '').trim();
        if (!val) return null; // optional
        if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(val)) {
          return t("val_email_invalid") || "Please enter a valid email address";
        }
        return null;
      }
      case 'basic_salary': {
        const strVal = String(value ?? '').trim();
        if (strVal === "") return t("val_basic_required") || "Basic salary is required";
        const num = parseFloat(strVal);
        if (isNaN(num) || num < 0) return t("val_basic_invalid") || "Basic salary must be a valid positive amount";
        return null;
      }
      case 'hra':
      case 'travel_allowance':
      case 'pf_deduction':
      case 'esi_deduction':
      case 'tds': {
        const strVal = String(value ?? '').trim();
        if (!strVal) return null;
        const num = parseFloat(strVal);
        if (isNaN(num) || num < 0) return t("val_amount_invalid") || "Amount must be a valid non-negative number";
        return null;
      }
      case 'account_number': {
        const val = (value ?? '').trim();
        if (!val) return null; // optional
        if (!/^\d{9,18}$/.test(val)) {
          return t("val_account_invalid") || "Account number must contain only digits (9-18 digits)";
        }
        return null;
      }
      case 'ifsc_code': {
        const val = (value ?? '').trim();
        if (!val) return null; // optional
        if (!/^[A-Z]{4}0[A-Z0-9]{6}$/i.test(val)) {
          return t("val_ifsc_invalid") || "IFSC code must be in valid format (e.g. SBIN0001234)";
        }
        return null;
      }
      case 'bank_name': {
        const val = (value ?? '').trim();
        if (!val) return null; // optional
        if (!/^[\p{L}\s.'\-&]+$/u.test(val)) {
          return t("val_bank_name_invalid") || "Bank name contains invalid characters";
        }
        return null;
      }
      default:
        return null;
    }
  };

  const validateStaffForm = (formData = sForm) => {
    const errs = {};
    const fieldsToValidate = [
      'name', 'email', 'basic_salary', 'hra', 'travel_allowance',
      'pf_deduction', 'esi_deduction', 'tds', 'bank_name', 'account_number', 'ifsc_code'
    ];

    fieldsToValidate.forEach(field => {
      const err = validateField(field, formData[field], formData);
      if (err) errs[field] = err;
    });

    // Check if total deductions exceed total earnings
    const basic = parseFloat(formData.basic_salary) || 0;
    const hra = parseFloat(formData.hra) || 0;
    const ta = parseFloat(formData.travel_allowance) || 0;
    const pf = parseFloat(formData.pf_deduction) || 0;
    const esi = parseFloat(formData.esi_deduction) || 0;
    const tds = parseFloat(formData.tds) || 0;

    const totalEarnings = basic + hra + ta;
    const totalDeductions = pf + esi + tds;

    if (!errs.basic_salary && totalEarnings < totalDeductions) {
      errs.net_pay = t("val_net_pay_invalid") || "Total deductions cannot exceed total earnings";
    }

    return errs;
  };

  const ss = k => e => {
    let val = e.target.value;
    if (k === 'ifsc_code') {
      val = val.toUpperCase();
    }
    const updatedForm = { ...sForm, [k]: val };
    setSForm(updatedForm);

    if (sErrors[k]) {
      const err = validateField(k, val, updatedForm);
      setSErrors(prev => {
        const next = { ...prev };
        if (err) {
          next[k] = err;
        } else {
          delete next[k];
        }
        return next;
      });
    }

    if (['basic_salary', 'hra', 'travel_allowance', 'pf_deduction', 'esi_deduction', 'tds'].includes(k) && sErrors.net_pay) {
      const basic = parseFloat(updatedForm.basic_salary) || 0;
      const hra = parseFloat(updatedForm.hra) || 0;
      const ta = parseFloat(updatedForm.travel_allowance) || 0;
      const pf = parseFloat(updatedForm.pf_deduction) || 0;
      const esi = parseFloat(updatedForm.esi_deduction) || 0;
      const tds = parseFloat(updatedForm.tds) || 0;
      if (basic + hra + ta >= pf + esi + tds) {
        setSErrors(prev => {
          const next = { ...prev };
          delete next.net_pay;
          return next;
        });
      }
    }
  };

  const handleBlur = k => () => {
    const err = validateField(k, sForm[k], sForm);
    setSErrors(prev => {
      const next = { ...prev };
      if (err) {
        next[k] = err;
      } else {
        delete next[k];
      }
      return next;
    });
  };

  const [staff, setStaff] = useState([]);
  const [payrolls, setPayrolls] = useState([]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [staffRes, payrollRes, bankRes] = await Promise.all([
        SalaryService.getStaff(),
        SalaryService.getPayrolls(),
        api.get('/api/v1/bank/accounts')
      ]);
      setStaff(staffRes.data.map(s => ({
        ...s,
        avatar: s.name.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase()
      })));
      setPayrolls(payrollRes.data);
      setBankAccounts(bankRes.data);
    } catch (err) {
      console.error("Failed to fetch salary data", err);
      toast.error(t("toast_load_failed"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const SALARY_HISTORY = payrolls.reduce((acc, p) => {
    if (!acc[p.staff_id]) acc[p.staff_id] = [];
    acc[p.staff_id].push(p);
    return acc;
  }, {});

  const departments = ["Management", "Finance", "Security", "Housekeeping", "Maintenance", "Admin"];
  const roles = ["Society Manager", "Accountant", "Security Guard", "Housekeeper", "Gardener", "Receptionist", "Electrician", "Plumber"];
  const deptColors = { Management: "blue", Finance: "green", Security: "amber", Housekeeping: "cyan", Maintenance: "orange", Admin: "purple" };

  const totalPayroll = staff.filter(s => s.status === "Active").reduce((a, s) => a + parseFloat(s.basic_salary || 0) + parseFloat(s.hra || 0) + parseFloat(s.travel_allowance || 0) - parseFloat(s.pf_deduction || 0) - parseFloat(s.esi_deduction || 0) - parseFloat(s.tds || 0), 0);
  const pendingCount = payrolls.filter(h => h.status === "Pending").length;
  const filtered = staff.filter(s => !search || s.name.toLowerCase().includes(search.toLowerCase()) || s.role.toLowerCase().includes(search.toLowerCase()) || s.department.toLowerCase().includes(search.toLowerCase()));

  const saveStaff = async () => {
    const errs = validateStaffForm();
    if (Object.keys(errs).length > 0) {
      setSErrors(errs);
      const firstErrorMessage = errs.name || errs.email || errs.basic_salary || errs.net_pay || errs.account_number || errs.ifsc_code || errs.bank_name || Object.values(errs)[0];
      toast.error(firstErrorMessage);
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...sForm,
        name: sForm.name.trim(),
        email: sForm.email ? sForm.email.trim() : null,
        bank_name: sForm.bank_name ? sForm.bank_name.trim() : null,
        account_number: sForm.account_number ? sForm.account_number.trim() : null,
        ifsc_code: sForm.ifsc_code ? sForm.ifsc_code.trim().toUpperCase() : null,
        basic_salary: parseFloat(sForm.basic_salary) || 0,
        hra: sForm.hra ? parseFloat(sForm.hra) : 0,
        travel_allowance: sForm.travel_allowance ? parseFloat(sForm.travel_allowance) : 0,
        pf_deduction: sForm.pf_deduction ? parseFloat(sForm.pf_deduction) : 0,
        esi_deduction: sForm.esi_deduction ? parseFloat(sForm.esi_deduction) : 0,
        tds: sForm.tds ? parseFloat(sForm.tds) : 0,
        joined_date: sForm.joined_date || null,
      };
      if (editStaff) {
        await SalaryService.updateStaff(editStaff.id, payload);
        toast.success(t("toast_staff_updated"));
      } else {
        await SalaryService.addStaff(payload);
        toast.success(t("toast_staff_added"));
      }
      setStaffModal(false);
      setEditStaff(null);
      setSForm(defS);
      setSErrors({});
      fetchData();
    } catch (err) {
      console.error("Failed to save staff", err);
      if (err.response?.data?.errors) {
        const apiErrors = err.response.data.errors;
        const mappedErrors = {};
        Object.keys(apiErrors).forEach(key => {
          mappedErrors[key] = Array.isArray(apiErrors[key]) ? apiErrors[key][0] : apiErrors[key];
        });
        setSErrors(prev => ({ ...prev, ...mappedErrors }));
        const firstApiError = Object.values(mappedErrors)[0];
        toast.error(firstApiError || t("toast_staff_save_failed"));
      } else {
        const msg = err.response?.data?.message || err.message || t("toast_staff_save_failed");
        toast.error(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  const openEditStaff = s => {
    setEditStaff(s);
    setSForm({
      name: s.name,
      email: s.email || "",
      role: s.role,
      department: s.department,
      basic_salary: String(s.basic_salary),
      hra: String(s.hra),
      travel_allowance: String(s.travel_allowance),
      pf_deduction: String(s.pf_deduction),
      esi_deduction: String(s.esi_deduction),
      tds: String(s.tds),
      bank_name: s.bank_name || "",
      ifsc_code: s.ifsc_code || "",
      account_number: s.account_number || "",
      joined_date: s.joined_date ? s.joined_date.split('T')[0] : "",
      status: s.status
    });
    setSErrors({});
    setStaffModal(true);
  };

  return (
    <div className="fade-up space-y-4">
      {payslipModal && (() => {
        const sm = staff.find(s => s.id === payslipModal.staff_id);
        return sm ? <PayslipModal entry={payslipModal} staffMember={sm} onClose={() => setPayslipModal(null)} t={t} /> : null;
      })()}
      {payrunModal && (
        <RunPayrollModal
          staff={staff}
          totalPayroll={totalPayroll}
          bankAccounts={bankAccounts}
          onClose={() => setPayrunModal(false)}
          onSuccess={fetchData}
          t={t}
        />
      )}
      {delStaff && <ConfirmModal title={t("title_remove_staff")} message={t("msg_remove_staff", { name: delStaff.name })} onConfirm={async () => {
        setLoading(true);
        try {
          await SalaryService.deleteStaff(delStaff.id);
          toast.success(t("toast_staff_removed"));
          setDelStaff(null);
          fetchData();
        } catch (err) {
          console.error("Failed to delete staff", err);
          toast.error(err.message || t("toast_staff_remove_failed"));
        } finally {
          setLoading(false);
        }
      }} onCancel={() => setDelStaff(null)} />}

      {staffModal && (
        <Modal 
          title={editStaff ? t("title_edit_staff") : t("title_add_staff")} 
          onClose={() => { setStaffModal(false); setEditStaff(null); setSForm(defS); setSErrors({}); }} 
          wide
          footer={
            <FormActions 
              onSave={saveStaff} 
              onCancel={() => { setStaffModal(false); setEditStaff(null); setSForm(defS); setSErrors({}); }} 
              label={editStaff ? t("btn_update_staff") : t("btn_add_staff")} 
              loading={loading} 
              plain
            />
          }
        >
          <div className="text-xs font-bold text-muted uppercase tracking-wider mb-3 pb-2 border-b border-border">{t("header_personal_info")}</div>
          <FormRow cols={2}>
            <Field 
              label={t("label_full_name")} 
              value={sForm.name} 
              onChange={ss("name")} 
              onBlur={handleBlur("name")}
              error={sErrors.name}
              placeholder="e.g. Ramesh Kumar"
              required 
            />
            <Field 
              label={t("label_email")} 
              value={sForm.email} 
              onChange={ss("email")} 
              onBlur={handleBlur("email")}
              error={sErrors.email}
              placeholder={t("placeholder_email")} 
            />
          </FormRow>
          <FormRow cols={2}>
            <Field label={t("label_role")} type="select" value={sForm.role} onChange={ss("role")} options={roles.map(r => ({ value: r, label: t("role_" + r.toLowerCase().replace(/ /g, "_")) || r }))} />
            <Field label={t("label_department")} type="select" value={sForm.department} onChange={ss("department")} options={departments.map(d => ({ value: d, label: t("dept_" + d.toLowerCase()) || d }))} />
          </FormRow>
          <FormRow cols={2}>
            <Field 
              label={t("label_doj")} 
              type="date" 
              value={sForm.joined_date} 
              onChange={ss("joined_date")} 
              onBlur={handleBlur("joined_date")}
              error={sErrors.joined_date}
            />
            <Field label={t("label_status")} type="select" value={sForm.status} onChange={ss("status")} options={["Active", "Inactive", "On Leave"].map(s => ({ value: s, label: t("status_" + s.toLowerCase().replace(/ /g, "_")) || s }))} />
          </FormRow>

          <div className="text-xs font-bold text-muted uppercase tracking-wider mb-3 pb-2 border-b border-border mt-4">{t("header_salary_structure")}</div>
          <FormRow cols={3}>
            <Field 
              label={t("label_basic")} 
              type="number" 
              value={sForm.basic_salary} 
              onChange={ss("basic_salary")} 
              onBlur={handleBlur("basic_salary")}
              error={sErrors.basic_salary}
              placeholder="0"
              required 
            />
            <Field 
              label={t("label_hra")} 
              type="number" 
              value={sForm.hra} 
              onChange={ss("hra")} 
              onBlur={handleBlur("hra")}
              error={sErrors.hra}
              placeholder="0"
            />
            <Field 
              label={t("label_ta")} 
              type="number" 
              value={sForm.travel_allowance} 
              onChange={ss("travel_allowance")} 
              onBlur={handleBlur("travel_allowance")}
              error={sErrors.travel_allowance}
              placeholder="0"
            />
          </FormRow>
          <FormRow cols={3}>
            <Field 
              label={t("label_pf")} 
              type="number" 
              value={sForm.pf_deduction} 
              onChange={ss("pf_deduction")} 
              onBlur={handleBlur("pf_deduction")}
              error={sErrors.pf_deduction}
              placeholder="0"
            />
            <Field 
              label={t("label_esi")} 
              type="number" 
              value={sForm.esi_deduction} 
              onChange={ss("esi_deduction")} 
              onBlur={handleBlur("esi_deduction")}
              error={sErrors.esi_deduction}
              placeholder="0"
            />
            <Field 
              label={t("label_tds")} 
              type="number" 
              value={sForm.tds} 
              onChange={ss("tds")} 
              onBlur={handleBlur("tds")}
              error={sErrors.tds}
              placeholder="0"
            />
          </FormRow>
          {(sForm.basic_salary !== "" || sForm.hra !== "" || sForm.travel_allowance !== "" || sForm.pf_deduction !== "" || sForm.esi_deduction !== "" || sForm.tds !== "") && (() => {
            const basic = parseFloat(sForm.basic_salary) || 0;
            const hra = parseFloat(sForm.hra) || 0;
            const ta = parseFloat(sForm.travel_allowance) || 0;
            const pf = parseFloat(sForm.pf_deduction) || 0;
            const esi = parseFloat(sForm.esi_deduction) || 0;
            const tds = parseFloat(sForm.tds) || 0;
            const net = basic + hra + ta - pf - esi - tds;
            const isNegative = net < 0;

            return (
              <div className="space-y-1 mb-3">
                <div className={`flex items-center justify-between border rounded-lg px-4 py-2.5 text-sm ${
                  isNegative 
                    ? "bg-red-50 dark:bg-red-950/20 border-red-300 dark:border-red-800" 
                    : "bg-primary-light dark:bg-primary-dark/10 border-primary/20"
                }`}>
                  <span className={`font-semibold ${isNegative ? "text-red-700 dark:text-red-300" : "text-primary"}`}>
                    {t("label_net_pay")}
                  </span>
                  <span className={`font-black text-base ${isNegative ? "text-red-600 dark:text-red-400" : "text-primary"}`}>
                    ₹{net.toLocaleString()}
                  </span>
                </div>
                {(sErrors.net_pay || isNegative) && (
                  <p className="text-xs text-red-500 font-medium px-1">
                    {sErrors.net_pay || t("val_net_pay_invalid") || "Total deductions cannot exceed total earnings"}
                  </p>
                )}
              </div>
            );
          })()}

          <div className="text-xs font-bold text-muted uppercase tracking-wider mb-3 pb-2 border-b border-border mt-2">{t("header_bank_details")}</div>
          <FormRow cols={2}>
            <Field 
              label={t("label_bank_name")} 
              value={sForm.bank_name} 
              onChange={ss("bank_name")} 
              onBlur={handleBlur("bank_name")}
              error={sErrors.bank_name}
              placeholder={t("placeholder_bank")} 
            />
            <Field 
              label={t("label_account")} 
              value={sForm.account_number} 
              onChange={ss("account_number")} 
              onBlur={handleBlur("account_number")}
              error={sErrors.account_number}
              placeholder={t("placeholder_account")} 
            />
          </FormRow>
          <FormRow>
            <Field 
              label={t("label_ifsc")} 
              value={sForm.ifsc_code} 
              onChange={ss("ifsc_code")} 
              onBlur={handleBlur("ifsc_code")}
              error={sErrors.ifsc_code}
              placeholder={t("placeholder_ifsc")} 
            />
          </FormRow>
        </Modal>
      )}

      {histStaff && (
        <Modal title={t("title_salary_history", { name: histStaff.name })} onClose={() => setHistStaff(null)} wide>
          <div className="flex items-center gap-3 bg-slate-50 dark:bg-slate-700/10 border border-border rounded-xl p-4 mb-5">
            <div className="w-12 h-12 rounded-full gradient-primary flex items-center justify-center text-white font-bold">{histStaff.avatar}</div>
            <div className="flex-1">
              <div className="font-bold text-ink">{histStaff.name}</div>
              <div className="text-xs text-muted">{t("role_" + (histStaff.role || "").toLowerCase().replace(/ /g, "_")) || histStaff.role} · {t("dept_" + (histStaff.department || "").toLowerCase()) || histStaff.department} · {t("text_joined", { date: histStaff.joined_date ? formatDateByPreference(histStaff.joined_date) : t("text_na") })}</div>
            </div>
            <div className="text-right">
              <div className="text-xs text-muted">{t("text_current_basic")}</div>
              <div className="text-lg font-black text-ink">₹{parseFloat(histStaff.basic_salary).toLocaleString()}</div>
            </div>
          </div>
          {(SALARY_HISTORY[histStaff.id] || []).length === 0
            ? <div className="text-center py-10 text-muted text-sm">{t("msg_no_history")}</div>
            : (
              <div className="space-y-2">
                {(SALARY_HISTORY[histStaff.id] || []).map((h, i) => (
                  <div key={i} className="flex items-center gap-3 p-3.5 border border-border rounded-xl hover:border-primary/20 hover:bg-slate-50/60 hover:dark:bg-slate-700/60 transition-all group">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${h.status === "Paid" ? "bg-emerald-50" : "bg-amber-50"}`}>
                      <Icon name="receipt" size={14} className={h.status === "Paid" ? "text-emerald-600" : "text-amber-600"} />
                    </div>
                    <div className="flex-1">
                      <div className="text-sm font-semibold text-ink">{h.month}</div>
                      <div className="text-xs text-muted">{t("mode_" + (h.payment_mode || "").toLowerCase().replace(/ /g, "_")) || h.payment_mode}{h.note ? ` · ${h.note}` : ""}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-bold text-ink">₹{parseFloat(h.net_pay).toLocaleString()}</div>
                      <div className="text-sm text-muted">{h.payment_date ? `Paid ${formatDateByPreference(h.payment_date)}` : "Pending"}</div>
                    </div>
                    <Badge color={h.status === "Paid" ? "green" : "amber"} dot>{t("status_" + (h.status || "").toLowerCase().replace(/ /g, "_")) || h.status}</Badge>
                    <button onClick={() => setPayslipModal(h)} className="opacity-0 group-hover:opacity-100 transition-opacity">
                      <Btn xs icon="eye">{t("btn_slip")}</Btn>
                    </button>
                  </div>
                ))}
              </div>
            )}
        </Modal>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <MetricCard label={t("card_monthly_payroll")} value={`₹${(totalPayroll / 1000).toFixed(1)}k`} delta={t("delta_active_staff")} deltaUp icon="salary" accent="blue" />
        <MetricCard label={t("card_active_staff")} value={staff.filter(s => s.status === "Active").length} delta={t("delta_total", { count: staff.length })} deltaUp icon="users" accent="green" />
        <MetricCard label={t("card_pending_payouts")} value={pendingCount} delta={pendingCount > 0 ? t("delta_needs_action") : t("delta_all_cleared")} deltaUp={pendingCount === 0} icon="alert" accent={pendingCount > 0 ? "red" : "green"} />
        <MetricCard label={t("card_annual_bill")} value={`₹${((totalPayroll * 12) / 100000).toFixed(1)}L`} delta={t("delta_projected")} deltaUp icon="money" accent="amber" />
      </div>

      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <Tabs tabs={[t("tab_staff_directory"), t("tab_payroll_history")]} active={tab} onChange={setTab} />
        <div className="flex flex-wrap gap-2">
          <Btn sm icon="download" onClick={() => {
            const headers = ["Name", "Role", "Department", "Basic Salary", "HRA", "Net Pay", "Status"];
            const rows = staff.map(s => [
              s.name, s.role, s.department, s.basic_salary, s.hra,
              (parseFloat(s.basic_salary) + parseFloat(s.hra) + parseFloat(s.travel_allowance) - parseFloat(s.pf_deduction) - parseFloat(s.esi_deduction) - parseFloat(s.tds)),
              s.status
            ]);
            const csvContent = "data:text/csv;charset=utf-8," + [headers, ...rows].map(e => e.join(",")).join("\n");
            const encodedUri = encodeURI(csvContent);
            const link = document.createElement("a");
            link.setAttribute("href", encodedUri);
            link.setAttribute("download", "Staff_Payroll_Report.csv");
            document.body.appendChild(link);
            link.click();
            toast.success(t("toast_report_exported"));
          }}>{t("btn_export")}</Btn>
          <Btn sm icon="zap" onClick={() => setPayrunModal(true)} style={{ background: "linear-gradient(135deg,#10b981,#059669)", color: "#fff", border: "none", boxShadow: "0 2px 8px rgba(16,185,129,0.3)" }}>{t("btn_run_payroll")}</Btn>
          <Btn sm variant="primary" icon="plus" className="hidden lg:flex" onClick={() => { setEditStaff(null); setSForm(defS); setStaffModal(true); }}>{t("btn_add_staff")}</Btn>
        </div>
      </div>

      {tab === 0 && (
        <Card noPad>
          <div className="p-3 sm:px-5 sm:pt-5 sm:pb-3 flex flex-col sm:flex-row sm:items-center justify-between gap-2.5 sm:gap-3 border-b border-border">
            <SearchBar value={search} onChange={setSearch} placeholder={t("placeholder_search_staff")} className="w-full sm:w-72" />
            <div className="text-xs text-muted whitespace-nowrap self-end sm:self-auto shrink-0">{t("msg_staff_count", { count: filtered.length })}</div>
          </div>
          <div className="divide-y divide-slate-50">
            {filtered.length === 0 ? (
              <div className="py-20 text-center text-muted">
                <Icon name="users" size={40} className="mx-auto mb-3 opacity-20" />
                <div className="text-sm font-medium">{t("msg_no_staff")}</div>
                <div className="text-xs mt-1">{t("msg_add_first_staff")}</div>
              </div>
            ) : filtered.map(s => {
              const gross = parseFloat(s.basic_salary) + parseFloat(s.hra) + parseFloat(s.travel_allowance);
              const deductions = parseFloat(s.pf_deduction) + parseFloat(s.esi_deduction) + parseFloat(s.tds);
              const netPay = gross - deductions;
              return (
                <div key={s.id}>
                  {/* Desktop View */}
                  <div className="hidden lg:flex px-5 py-4 hover:bg-slate-50/60 hover:dark:bg-slate-700/60 transition-colors group items-center gap-4">
                    <div className={`w-11 h-11 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0 ${s.status === "Inactive" ? "bg-slate-300" : "gradient-primary"}`}>{s.avatar}</div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="font-semibold text-ink text-sm">{s.name}</span>
                        <Badge color={deptColors[s.department] || "gray"} sm>{t("dept_" + (s.department || "").toLowerCase()) || s.department}</Badge>
                        {s.status !== "Active" && <Badge color="gray" sm>{t("status_" + (s.status || "").toLowerCase().replace(/ /g, "_")) || s.status}</Badge>}
                      </div>
                      <div className="text-xs text-muted">{t("role_" + (s.role || "").toLowerCase().replace(/ /g, "_")) || s.role} · {s.email || t("text_no_email")} · {t("text_joined", { date: s.joined_date ? formatDateByPreference(s.joined_date) : t("text_na") })}</div>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <div className="text-center px-3 py-1.5 bg-emerald-50 dark:bg-emerald-900/30 rounded-lg">
                        <div className="text-sm text-emerald-600 dark:text-emerald-400 font-semibold uppercase tracking-wide">{t("text_gross")}</div>
                        <div className="text-xs font-bold text-ink dark:text-slate-300">₹{gross.toLocaleString()}</div>
                      </div>
                      <div className="text-center px-3 py-1.5 bg-red-50 dark:bg-red-900/30 rounded-lg">
                        <div className="text-sm text-red-600 dark:text-red-400 font-semibold uppercase tracking-wide">{t("text_deductions")}</div>
                        <div className="text-xs font-bold text-ink dark:text-slate-300">₹{deductions.toLocaleString()}</div>
                      </div>
                      <div className="text-center px-3 py-1.5 rounded-lg" style={{ background: "linear-gradient(135deg,rgba(79,70,229,.08),rgba(124,58,237,.08))" }}>
                        <div className="text-sm text-primary dark:text-blue-500 font-semibold uppercase tracking-wide">{t("text_net_pay")}</div>
                        <div className="text-xs font-bold text-primary dark:text-slate-300">₹{netPay.toLocaleString()}</div>
                      </div>
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      <Btn xs icon="eye" onClick={() => setHistStaff(s)}>{t("btn_history")}</Btn>
                      <Btn xs icon="edit" onClick={() => openEditStaff(s)}>{t("btn_edit")}</Btn>
                      <Btn xs variant="danger" icon="trash" onClick={() => setDelStaff(s)} />
                    </div>
                  </div>

                  {/* Mobile View */}
                  <div className="block lg:hidden px-3 py-2">
                    <IonItemSliding className="rounded-xl border border-slate-100 dark:border-slate-800 shadow-sm overflow-hidden" style={{ '--background': 'transparent' }}>
                      <IonItem lines="none" style={{ '--background': 'transparent', '--padding-start': '0', '--inner-padding-end': '0' }}>
                        <div className="flex w-full items-center gap-3 p-3 bg-white dark:bg-slate-900">
                          <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg flex-shrink-0 ${s.status === "Inactive" ? "bg-slate-300" : "gradient-primary"}`}>{s.avatar}</div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-bold text-ink text-sm truncate">{s.name}</span>
                              {s.status !== "Active" && <Badge color="gray" sm>{s.status}</Badge>}
                            </div>
                            <div className="text-xs text-muted mb-1 leading-tight">
                              {s.role} · {s.department}
                            </div>
                            <div className="text-xs font-bold text-primary">₹{netPay.toLocaleString()}</div>
                          </div>
                          <div className="text-slate-300 ml-1">
                            <Icon name="chevron_left" size={14} />
                          </div>
                        </div>
                      </IonItem>
                      <IonItemOptions side="end">
                        <IonItemOption color="tertiary" onClick={() => setHistStaff(s)}>
                          <div className="flex flex-col items-center justify-center w-12">
                            <Icon name="eye" size={20} color="white" />
                          </div>
                        </IonItemOption>
                        <IonItemOption color="primary" onClick={() => openEditStaff(s)}>
                          <div className="flex flex-col items-center justify-center w-12">
                            <Icon name="edit" size={20} color="white" />
                          </div>
                        </IonItemOption>
                        <IonItemOption color="danger" onClick={() => setDelStaff(s)}>
                          <div className="flex flex-col items-center justify-center w-12">
                            <Icon name="trash" size={20} color="white" />
                          </div>
                        </IonItemOption>
                      </IonItemOptions>
                    </IonItemSliding>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {tab === 1 && (
        <Card>
          <SectionHeader title={t("title_payroll_history")} subtitle={t("subtitle_payroll_history")} />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-5">
            {payrolls.length === 0 ? (
              <div className="col-span-full py-10 text-center border-2 border-dashed border-slate-200 dark:border-slate-800/60 rounded-xl">
                <Icon name="salary" size={30} className="mx-auto mb-2 opacity-20 dark:opacity-40 text-slate-400 dark:text-slate-500" />
                <div className="text-xs text-muted dark:text-slate-400">{t("msg_no_payroll_runs")}</div>
              </div>
            ) : Array.from(new Set(payrolls.map(p => p.month))).map((month, i) => {
              const monthPayrolls = payrolls.filter(p => p.month === month);
              const total = monthPayrolls.reduce((a, p) => a + parseFloat(p.net_pay), 0);
              const paid = monthPayrolls.filter(p => p.status === "Paid").length;
              const pending = monthPayrolls.filter(p => p.status === "Pending").length;
              return (
                <div key={i} className="border border-border rounded-xl p-4 hover:border-primary/20 hover:shadow-card transition-all">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <div className="text-sm font-bold text-ink">{month}</div>
                      <div className="text-xs text-muted mt-0.5">{t("msg_staff_processed", { count: monthPayrolls.length })}</div>
                    </div>
                    <Badge color={pending === 0 ? "green" : "amber"} dot>{pending === 0 ? t("text_completed") : t("text_in_progress")}</Badge>
                  </div>
                  <div className="text-2xl font-black text-ink mb-3">₹{total.toLocaleString()}</div>
                  <div className="flex gap-2 text-xs">
                    <span className="flex items-center gap-1 text-emerald-600 font-semibold"><Icon name="check" size={11} color="#10b981" />{paid} {t("text_paid").toLowerCase()}</span>
                    {pending > 0 && <span className="flex items-center gap-1 text-amber-600 font-semibold"><Icon name="alert" size={11} color="#f59e0b" />{pending} {t("text_pending").toLowerCase()}</span>}
                  </div>
                </div>
              );
            })}
          </div>

          <SectionHeader title={t("title_individual_records")} subtitle={t("subtitle_individual_records")} />
          <div className="hidden lg:block">
            <DataTable
              cols={[t("col_staff"), t("col_dept"), t("col_month"), t("col_gross"), t("col_deductions"), t("col_net_pay"), t("col_mode"), t("col_status"), t("col_actions")]}
              rows={payrolls.length === 0 ? [] : payrolls.slice(0, 10).map(h => {
                const sm = staff.find(s => s.id === h.staff_id);
                if (!sm) return [];
                const gross = parseFloat(h.basic_salary) + parseFloat(h.hra) + parseFloat(h.travel_allowance);
                const deductions = parseFloat(h.pf_deduction) + parseFloat(h.esi_deduction) + parseFloat(h.tds);
                return [
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-full gradient-primary flex items-center justify-center text-white text-sm font-bold">{sm.avatar}</div>
                    <span className="text-xs font-semibold text-ink">{sm.name}</span>
                  </div>,
                  <Badge color={deptColors[sm.department] || "gray"} sm>{sm.department}</Badge>,
                  h.month,
                  <span className="text-emerald-600 font-semibold">₹{gross.toLocaleString()}</span>,
                  <span className="text-red-500">₹{deductions.toLocaleString()}</span>,
                  <span className="font-bold text-ink">₹{parseFloat(h.net_pay).toLocaleString()}</span>,
                  h.payment_mode,
                  <Badge color={h.status === "Paid" ? "green" : "amber"} dot>{t("status_" + (h.status || "").toLowerCase().replace(/ /g, "_")) || h.status}</Badge>,
                  <Btn xs icon="eye" onClick={() => setPayslipModal(h)}>{t("btn_payslip")}</Btn>,
                ];
              })}
            />
          </div>
          
          {/* Mobile View Payrolls */}
          <div className="block lg:hidden space-y-3 mt-4">
            {payrolls.slice(0, 10).map((h, i) => {
              const sm = staff.find(s => s.id === h.staff_id);
              if (!sm) return null;
              
              return (
                <div key={h.id} className="rounded-xl border border-slate-100 dark:border-slate-800 shadow-sm bg-white dark:bg-slate-900 overflow-hidden">
                  <div className="flex w-full items-center gap-3 p-3">
                    <div className="w-10 h-10 rounded-full gradient-primary flex items-center justify-center text-white font-bold text-base flex-shrink-0">
                      {sm.avatar}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-bold text-ink text-sm truncate">{sm.name}</span>
                      </div>
                      <div className="text-xs text-muted mb-1 leading-tight">
                        {sm.department} · {h.month}
                      </div>
                      <div className="flex items-center gap-1.5 mt-1.5">
                        <Badge color={h.status === "Paid" ? "green" : "amber"} dot sm>{h.status}</Badge>
                      </div>
                    </div>

                    <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
                      <div className="text-sm font-bold text-ink">₹{parseFloat(h.net_pay).toLocaleString()}</div>
                      <Btn xs icon="eye" onClick={() => setPayslipModal(h)}>{t("btn_slip")}</Btn>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {payrolls.length === 0 && <div className="text-center py-10 text-xs text-muted">{t("msg_no_records")}</div>}
        </Card>
      )}

      {/* FAB for Mobile */}
      {tab === 0 && !staffModal && !delStaff && !histStaff && !payrunModal && !payslipModal && createPortal(
        <div className="fixed bottom-6 right-6 z-50 lg:hidden pointer-events-auto">
          <button
            onClick={() => { setEditStaff(null); setSForm(defS); setStaffModal(true); }}
            className="w-14 h-14 bg-primary text-white rounded-full flex items-center justify-center shadow-[0_8px_30px_rgb(0,0,0,0.12)] hover:scale-105 transition-transform active:scale-95 border-0 cursor-pointer"
          >
            <Icon name="plus" size={24} />
          </button>
        </div>,
        document.body
      )}

    </div>
  );
}

// Sub-components moved outside to prevent unmounting on parent re-render
const PayslipModal = ({ entry, staffMember, onClose, t }) => {
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const gross = parseFloat(entry.basic_salary) + parseFloat(entry.hra) + parseFloat(entry.travel_allowance);
  const deductions = parseFloat(entry.pf_deduction) + parseFloat(entry.esi_deduction) + parseFloat(entry.tds);
  const netPay = parseFloat(entry.net_pay);

  const download = async () => {
    setDownloadLoading(true);
    try {
      const res = await SalaryService.downloadPayslip(entry.id);
      const url = window.URL.createObjectURL(new Blob([res]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Payslip_${staffMember.name.replace(/\s+/g, '_')}_${entry.month.replace(/\s+/g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(t("toast_payslip_downloaded"));
    } catch (err) {
      console.error("Download failed", err);
      toast.error(err.message || t("toast_payslip_download_failed"));
    } finally {
      setDownloadLoading(false);
    }
  };

  const sendEmail = async () => {
    setEmailLoading(true);
    try {
      const res = await SalaryService.sendPayslipEmail(entry.id);
      setSent(true);
      toast.success(res.message || t("toast_payslip_sent"));
      setTimeout(() => setSent(false), 3000);
    } catch (err) {
      console.error("Email failed", err);
      toast.error(err.message || t("toast_payslip_send_failed"));
    } finally {
      setEmailLoading(false);
    }
  };

  return (
    <Modal title={t("title_payslip")} onClose={onClose} wide>
      <div className="rounded-xl overflow-hidden mb-5 shadow-lg" style={{ background: "linear-gradient(135deg,#0f172a 0%,#1e1b4b 100%)" }}>
        <div className="flex items-center justify-between p-5">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <div className="w-7 h-7 rounded-lg gradient-primary flex items-center justify-center text-white font-black text-xs">SA</div>
              <span className="text-white font-bold text-sm">The Clover App</span>
            </div>
            <div className="text-white/40 text-sm uppercase tracking-widest">{t("text_disbursement_advice")}</div>
          </div>
          <div className="text-right">
            <div className="text-white/50 text-xs uppercase tracking-wider mb-0.5">{t("text_pay_period")}</div>
            <div className="text-white font-bold text-sm">{entry.month}</div>
          </div>
        </div>
        <div className="border-t border-white/10 px-5 py-4 flex items-center gap-3 bg-white/5">
          <div className="w-11 h-11 rounded-full gradient-primary flex items-center justify-center text-white font-bold text-sm shadow-inner">{staffMember.avatar}</div>
          <div>
            <div className="text-white font-bold">{staffMember.name}</div>
            <div className="text-white/50 text-xs">{t("role_" + (staffMember.role || "").toLowerCase().replace(/ /g, "_")) || staffMember.role} · {t("dept_" + (staffMember.department || "").toLowerCase()) || staffMember.department}</div>
          </div>
          <div className="ml-auto text-right">
            <div className="text-white/50 text-sm uppercase tracking-wider">{t("text_employee_id")}</div>
            <div className="text-white text-xs font-mono font-bold tracking-tighter">{String(staffMember.id).padStart(6, '0')}</div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
        <div className="bg-slate-900/70 dark:bg-slate-900 border border-slate-700/80 rounded-xl p-4 shadow-[0_10px_30px_rgba(0,0,0,0.18)]">
          <div className="text-sm font-bold text-slate-400 dark:text-slate-300 uppercase tracking-widest mb-3 flex items-center gap-1.5">
            <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> {t("text_earnings")}
          </div>
          {[['Basic Salary', entry.basic_salary], ['HRA', entry.hra], ['Travel Allowance', entry.travel_allowance]].map(([l, v]) => (
            <div key={l} className="flex justify-between py-2 border-b border-slate-700/70 last:border-0 text-sm">
              <span className="text-slate-300">{l}</span>
              <span className="font-semibold text-slate-100">₹{parseFloat(v).toLocaleString()}</span>
            </div>
          ))}
          <div className="flex justify-between pt-2">
            <span className="text-xs font-bold text-slate-100 uppercase">{t("text_gross_earnings")}</span>
            <span className="font-black text-emerald-600">₹{gross.toLocaleString()}</span>
          </div>
        </div>
        <div className="bg-slate-900/70 dark:bg-slate-900 border border-slate-700/80 rounded-xl p-4 shadow-[0_10px_30px_rgba(0,0,0,0.18)]">
          <div className="text-sm font-bold text-slate-400 dark:text-slate-300 uppercase tracking-widest mb-3 flex items-center gap-1.5">
            <div className="w-1.5 h-1.5 rounded-full bg-red-500" /> {t("text_deductions")}
          </div>
          {[['PF Deduction', entry.pf_deduction], ['ESI Deduction', entry.esi_deduction], ['TDS', entry.tds]].map(([l, v]) => (
            <div key={l} className="flex justify-between py-2 border-b border-slate-700/70 last:border-0 text-sm">
              <span className="text-slate-300">{l}</span>
              <span className="font-semibold text-red-600">₹{parseFloat(v).toLocaleString()}</span>
            </div>
          ))}
          <div className="flex justify-between pt-2">
            <span className="text-xs font-bold text-slate-100 uppercase">{t("text_total_deductions")}</span>
            <span className="font-black text-red-600">₹{deductions.toLocaleString()}</span>
          </div>
        </div>
      </div>

      <div className="rounded-xl p-5 flex items-center justify-between mb-5 shadow-sm" style={{ background: "linear-gradient(135deg,#4f46e5,#7c3aed)" }}>
        <div>
          <div className="text-white/70 text-sm font-bold uppercase tracking-widest mb-1">{t("text_net_payable")}</div>
          <div className="text-white text-3xl font-black tracking-tight">₹{netPay.toLocaleString()}</div>
        </div>
        <div className="text-right flex flex-col items-end">
          <div className="text-white/70 text-sm font-bold uppercase tracking-widest mb-1">{t("text_payment_status")}</div>
          <div className={`text-xs font-black px-4 py-1.5 rounded-full shadow-lg backdrop-blur-md w-fit ${entry.status === "Paid" ? "bg-white/20 text-white" : "bg-amber-400/30 text-amber-100"}`}>
            {(t("status_" + (entry.status || "").toLowerCase().replace(/ /g, "_")) || entry.status).toUpperCase()}
          </div>
        </div>
      </div>

      {entry.note && (
        <div className="bg-slate-900/70 border border-slate-700 rounded-lg px-4 py-3 text-xs text-slate-300 mb-5 flex items-start gap-3">
          <Icon name="info" size={14} className="text-blue-400 flex-shrink-0 mt-0.5" />
          <div>
            <span className="font-bold uppercase text-sm block mb-0.5 text-slate-400">{t("text_admin_note")}</span>
            {entry.note}
          </div>
        </div>
      )}

      <div className="flex gap-3">
        <Btn variant="primary" icon="download" full onClick={download} loading={downloadLoading}>{t("btn_download_pdf")}</Btn>
        <Btn variant={sent ? "success" : "secondary"} icon={sent ? "check" : "mail"} full onClick={sendEmail} loading={emailLoading}>{sent ? t("btn_email_sent") : t("btn_send_email")}</Btn>
      </div>
    </Modal>
  );
};

const RunPayrollModal = ({ staff, totalPayroll, bankAccounts, onClose, onSuccess, t }) => {
  useEffect(() => {
    if (bankAccounts.length === 0) {
      setMode("Cash");
      setBankId("");
      return;
    }

    setBankId(prev => prev || bankAccounts.find(b => b.is_primary)?.id || bankAccounts[0]?.id || "");
    setMode(prev => prev === "Cash" ? "Cash" : "Bank Transfer");
  }, [bankAccounts]);

  const getMonths = () => {
    const months = [];
    for (let i = 0; i < 6; i++) {
      const d = new Date();
      d.setMonth(d.getMonth() - i);
      months.push(d.toLocaleString('default', { month: 'long', year: 'numeric' }));
    }
    return months;
  };

  const monthOptions = getMonths();
  const [month, setMonth] = useState(monthOptions[0]);
  const [mode, setMode] = useState(bankAccounts.length > 0 ? "Bank Transfer" : "Cash");
  const [bankId, setBankId] = useState(bankAccounts.find(b => b.is_primary)?.id || (bankAccounts[0]?.id || ""));
  const [running, setRunning] = useState(false);
  const [done, setDone] = useState(false);
  const activeStaff = staff.filter(s => s.status === "Active");

  const run = async () => {
    setRunning(true);
    try {
      const res = await SalaryService.runPayroll({ month, payment_mode: mode, bank_account_id: bankId });
      setDone(true);
      toast.success(res.message || t("toast_payroll_processed"));
      if (onSuccess) onSuccess();
    } catch (err) {
      console.error("Failed to run payroll", err);
      toast.error(err.message || t("toast_payroll_failed"));
    } finally {
      setRunning(false);
    }
  };

  return (
    <Modal title={t("title_run_payroll")} onClose={onClose}>
      {done ? (
        <div className="text-center py-8">
          <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-5 shadow-inner animate-in zoom-in duration-300">
            <Icon name="check" size={36} color="#10b981" strokeWidth={3} />
          </div>
          <div className="text-2xl font-black text-ink mb-2">{t("text_payroll_successful")}</div>
          <div className="text-sm text-muted mb-8 max-w-[280px] mx-auto leading-relaxed">
            <span dangerouslySetInnerHTML={{ __html: t("text_salaries_processed", { count: activeStaff.length, month }) }} />
          </div>
          <Btn full variant="primary" onClick={onClose} className="h-12 text-base shadow-lg shadow-primary/20">{t("btn_done")}</Btn>
        </div>
      ) : (
        <>
          <div className="bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-5 mb-5 shadow-inner">
            <div className="text-sm font-bold text-slate-500 dark:text-slate-300 uppercase tracking-widest mb-4">{t("text_immediate_summary")}</div>
            <div className="grid grid-cols-3 gap-3">
              {[
                [t("text_staff"), activeStaff.length, "blue"],
                [t("text_disburse"), `₹${(totalPayroll / 1000).toFixed(1)}k`, "green"],
                [t("text_cycle"), month.split(" ")[0], "purple"]
              ].map(([l, v, c]) => (
                <div key={l} className="text-center">
                  <div className={`text-lg font-black ${c === "blue" ? "text-blue-700 dark:text-blue-400" : c === "green" ? "text-emerald-700 dark:text-emerald-400" : "text-purple-700 dark:text-purple-400"}`}>{v}</div>
                  <div className="text-sm font-bold text-slate-400 uppercase tracking-tighter mt-1">{l}</div>
                </div>
              ))}
            </div>
          </div>
          <FormRow cols={2}>
            <Field label={t("label_pay_month")} type="select" value={month} onChange={e => setMonth(e.target.value)} options={monthOptions} />
            <Field label={t("label_payment_mode")} type="select" value={mode} onChange={e => setMode(e.target.value)} options={["Bank Transfer", "Cash", "Cheque"].map(m => ({ value: m, label: t("mode_" + m.toLowerCase().replace(/ /g, "_")) || m }))} />
          </FormRow>
          {mode === "Bank Transfer" && (
            <FormRow>
              <Field label={t("label_source_bank")} type="select" value={bankId} onChange={e => setBankId(e.target.value)} options={bankAccounts.map(b => ({ label: `${b.name} (${b.account_type}) - ₹${parseFloat(b.current_balance).toLocaleString('en-IN')}`, value: b.id }))} />
            </FormRow>
          )}
          <div className="bg-amber-50/80 border border-amber-200/60 rounded-xl px-4 py-4 text-xs text-amber-900 flex items-start gap-3 mb-6 shadow-sm">
            <Icon name="alert" size={16} className="text-amber-500 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-bold text-[11px] uppercase tracking-wider mb-1">{t("text_confirmation_req")}</p>
              {t("text_confirmation_msg")}
            </div>
          </div>
          <FormActions
            onSave={run}
            onCancel={onClose}
            label={running ? t("btn_processing") : t("btn_generate_payroll")}
            loading={running}
          />
        </>
      )}
    </Modal>
  );
};
